import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { Edit, Delete, Add, ArrowBack, Info } from "@mui/icons-material";
import { MenuItem } from "@mui/material";
import {
  Button,
  Table,
  TableBody,
  IconButton,
  TableCell,
  TableContainer,
  Tooltip,
  TableHead,
  TableRow,
  Card,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
} from "@mui/material";

const LeavePage = () => {
  // State variables for leave requests and form
  const [currentPage, setCurrentPage] = useState(1);
  const [leaveRequests, setLeaveRequests] = useState([]); // Fetched from backend
  const [selectedLeaves, setSelectedLeaves] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [formData, setFormData] = useState({
    leave_type: "",
    status: "",
    startDate: "",
    endDate: "",
    leaveComments: "",
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState("");

  const leavesPerPage = 10;
  const totalPages = Math.ceil(leaveRequests.length / leavesPerPage);
  const displayedLeaves = leaveRequests.slice(
    (currentPage - 1) * leavesPerPage,
    currentPage * leavesPerPage
  );

  // Base URL for your leave API endpoints
  const BASE_API_URL = "https://attendanceapi.gensparc.com/leave";

  // Fetch leave requests for the current user
  const fetchLeaveRequests = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${BASE_API_URL}/allleavelist`, config);
      setLeaveRequests(response.data);
    } catch (error) {
      console.error("Error fetching leave requests:", error);
    }
  };

  // Helper function to convert a date string to YYYY-MM-DD format
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date)) return "";
  const year = date.getFullYear();
  let month = (date.getMonth() + 1).toString();
  if (month.length < 2) month = "0" + month;
  let day = date.getDate().toString();
  if (day.length < 2) day = "0" + day;
  return `${year}-${month}-${day}`;
};

  useEffect(() => {
    fetchLeaveRequests();
  }, []);

  // Reset current page if needed
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [leaveRequests, totalPages, currentPage]);

  const handlePageChange = (page) => setCurrentPage(page);

  const handleSelectLeave = useCallback((leaveId) => {
    setSelectedLeaves((prev) =>
      prev.includes(leaveId)
        ? prev.filter((id) => id !== leaveId)
        : [...prev, leaveId]
    );
  }, []);

  // Delete leave requests (calls DELETE endpoint for each selected leave)
  const handleDelete = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      for (const id of selectedLeaves) {
        await axios.delete(`${BASE_API_URL}/delete/${id}`, config);
      }
      fetchLeaveRequests();
      setSelectedLeaves([]);
    } catch (error) {
      console.error("Error deleting leave request(s):", error);
    }
  }, [selectedLeaves]);

  // Handle clicking the Apply/Edit button: open the dialog with pre-filled form if editing
  const handleAddLeaveClick = (leave = null) => {
    if (leave) {
      setFormData({
        leave_type: leave.leave_type,
        status: leave.status,
        startDate: formatDate(leave.start_date),  //pre-filled
        endDate: formatDate(leave.end_date),  //pre-filled 
        leaveComments: leave.leaveComments || "",
      });
    } else {
      setFormData({
        leave_type: "",
        status: "",
        startDate: "",
        endDate: "",
        leaveComments: "",
      });
    }
    setEditingLeave(leave);
    setShowAddForm(true);
  };

  // Updated handleFormSubmit to use PUT for updating and POST for creating new leave
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = {
        leave_type: formData.leave_type || "General",
        status: formData.status,
        start_date: formData.startDate,
        end_date: formData.endDate,
        leaveComments: formData.leaveComments,
      };
      

      if (editingLeave) {
        // Update existing leave request (ensure your backend supports this endpoint)
        await axios.put(`${BASE_API_URL}/update/${editingLeave.id}`, payload, config);
      } else {
        // Create new leave request
        await axios.post(`${BASE_API_URL}/request`, payload, config);
      }

      fetchLeaveRequests();
      setShowAddForm(false);
      setEditingLeave(null);
      setFormData({
        leave_type: "",
        status: "",
        startDate: "",
        endDate: "",
        leaveComments: "",
      });
    } catch (error) {
      console.error("Error submitting leave request:", error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCellClick = (content) => {
    setDialogContent(content);
    setOpenDialog(true);
  };

  const commonStyles = {
    fontWeight: "bold",
    fontSize: "12px",
    whiteSpace: "nowrap",
    padding: "8px",
    textAlign: "center",
    maxWidth: "200px",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  

  // for info tooltip in table cells
    const renderTableCell = (content) => {
      if (!content) return "";
  
      const stringContent = content.toString();
      const isLong = stringContent.length > 20;
  
      return isLong ? (
        <Tooltip title={stringContent} arrow>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              cursor: "default",
            }}
          >
            {stringContent.substring(0, 20)}...
            <Info fontSize="small" style={{ marginLeft: 4, color: "#888" }} />
          </span>
        </Tooltip>
      ) : (
        stringContent
      );
    };

  return (
    <div style={{ marginRight: "0", paddingLeft: "5px", paddingRight: "5px" }}>
      <div className="container-fluid px-0 py-5" style={{ marginRight: "0" }}>
        <Card sx={{ padding: 3, marginBottom: 3, boxShadow: 3 }}>
          <div className="d-flex justify-content-between align-items-center">
            <Typography variant="h4" component="h1" color="textPrimary" sx={{ fontWeight: 600 }}>
              Team Leave Management
            </Typography>
            <div>
              {/* <Button
                onClick={() => handleAddLeaveClick()}
                variant="contained"
                color="primary"
                startIcon={<Add />}
                sx={{
                  paddingX: 2,
                  paddingY: 1,
                  fontSize: "12px",
                  borderRadius: "15px",
                  boxShadow: 2,
                  "&:hover": { boxShadow: 4 },
                  marginRight: 1,
                }}
              >
                Apply Leave
              </Button> */}
              <Button
                onClick={handleDelete}
                variant="contained"
                color="error"
                startIcon={<Delete />}
                disabled={selectedLeaves.length === 0}
                sx={{
                  paddingX: 2,
                  paddingY: 1,
                  fontSize: "12px",
                  borderRadius: "15px",
                  boxShadow: 2,
                  "&:hover": { boxShadow: 4 },
                  marginRight: 1,
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </Card>

        <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: "#f4f5f7" }}>
              <TableRow>
                <TableCell sx={commonStyles}>
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      setSelectedLeaves(e.target.checked ? leaveRequests.map((leave) => leave.id) : []);
                    }}
                    checked={selectedLeaves.length > 0 && selectedLeaves.length === leaveRequests.length}
                  />
                </TableCell>
                {["Employee", "Leave Type", "Start Date", "End Date", "No. Of Days", "Applied Date", "Status", "Action"].map((heading, index) => (
                  <TableCell key={index} sx={commonStyles}>
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedLeaves.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={commonStyles}>
                    No leave requests found
                  </TableCell>
                </TableRow>
              ) : (
                displayedLeaves.map((leave) => (
                  <TableRow key={leave.id}>
                    <TableCell sx={commonStyles}>
                      <input
                        type="checkbox"
                        checked={selectedLeaves.includes(leave.id)}
                        onChange={() => handleSelectLeave(leave.id)}
                      />
                    </TableCell>
                    <TableCell sx={commonStyles}>{renderTableCell(leave.employee_name)}</TableCell>
                    <TableCell sx={commonStyles}>{renderTableCell(leave.leave_type)}</TableCell>
                    <TableCell sx={commonStyles}>{renderTableCell(leave.start_date)}</TableCell>
                    <TableCell sx={commonStyles}>{renderTableCell(leave.end_date)}</TableCell>
                    <TableCell sx={commonStyles}>{renderTableCell(leave.days)}</TableCell>
                    <TableCell sx={commonStyles}>{renderTableCell(leave.date)}</TableCell>
                    <TableCell sx={commonStyles}>{renderTableCell(leave.status || "Pending")}</TableCell>
                    <TableCell sx={commonStyles}>
                      <IconButton
                        sx={{
                          backgroundColor: "#ffeb3b",
                          borderRadius: "10px",
                          "&:hover": { backgroundColor: "#fbc02d" },
                        }}
                        onClick={() => handleAddLeaveClick(leave)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <div className="d-flex justify-content-between align-items-center mt-4">
          <Typography variant="body1">
            Page {currentPage} of {totalPages}
          </Typography>
          <div>
            <Button
              variant="outlined"
              color="primary"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              sx={{
                marginRight: 1,
                borderRadius: "20px",
                paddingX: 1,
                paddingY: 0.5,
                fontSize: "12px",
              }}
              aria-label="Previous Page"
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              color="primary"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              sx={{
                borderRadius: "20px",
                paddingX: 1,
                paddingY: 0.5,
                fontSize: "12px",
              }}
              aria-label="Next Page"
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* Dialog for Full Content */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>Full Content</DialogTitle>
        <DialogContent>
          <p>{dialogContent}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Leave Dialog */}
      <Dialog open={showAddForm} onClose={() => setShowAddForm(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={() => setShowAddForm(false)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6">{editingLeave ? "Edit Leave" : "Apply Leave"}</Typography>
        </DialogTitle>

        <Box component="form" onSubmit={handleFormSubmit} sx={{ p: 3 }}>
          <TextField
            fullWidth
            label="Leave Type*"
            name="leave_type"
            value={formData.leave_type}
            onChange={handleInputChange}
            margin="normal"
            required
          />
         <TextField
      fullWidth
      select
      label="Status"
      name="status"
      value={formData.status}
      onChange={handleInputChange}
      margin="normal"
      required
    >
      <MenuItem value="Approved">Approved</MenuItem>
      <MenuItem value="Pending">Pending</MenuItem>
      <MenuItem value="Not Approved">Not Approved</MenuItem>
    </TextField>
          <TextField
            fullWidth
            label="Start Date*"
            name="startDate"
            type="date"
            value={formData.startDate}
            onChange={handleInputChange}
            margin="normal"
            required
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="End Date*"
            name="endDate"
            type="date"
            value={formData.endDate}
            onChange={handleInputChange}
            margin="normal"
            required
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Leave Comments"
            name="leaveComments"
            value={formData.leaveComments}
            onChange={handleInputChange}
            margin="normal"
            multiline
            rows={4}
            helperText={`${formData.leaveComments.length} / 250`}
          />

          <DialogActions sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              {editingLeave ? "Update" : "Submit"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </div>
  );
};

export default LeavePage;
